import { OverlayContainer } from '@angular/cdk/overlay';
import { Injectable, inject } from '@angular/core';
import { MatSelect } from '@angular/material/select';

/**
 * Globally injects a search input at the top of every Material `mat-select`
 * panel as it opens. No template changes required — a MutationObserver
 * watches the CDK overlay container and decorates each `.mat-mdc-select-panel`
 * the moment it appears.
 *
 * Three states:
 *  1. Panel has options  → search bar shown, options filterable
 *  2. Searching, no hits → "No matching results" row (styled as disabled mat-option)
 *  3. Panel has 0 options → "No data is available" row, no search bar
 *
 * Opt-out: add `panelClass="no-search"` to any mat-select to skip decoration.
 */
@Injectable({ providedIn: 'root' })
export class MatSelectSearchService {
  private static readonly OPT_OUT_CLASS = 'no-search';
  private static readonly MARK_CLASS = 'app-select-search-attached';

  private overlayContainer = inject(OverlayContainer);
  private observer?: MutationObserver;

  /** Stores the applyFilter fn per panel so the reuse path can reset state. */
  private readonly filterFns = new WeakMap<HTMLElement, (term: string) => void>();

  init(): void {
    if (typeof document === 'undefined' || this.observer) return;

    // Angular Material's _canOpen() has an `!!this.options?.length` guard that
    // prevents the panel from opening when there are 0 options. Patch the
    // prototype once so empty selects can open — our attachSearch then shows
    // the "No data is available" row and hides the search bar automatically.
    const proto = MatSelect.prototype as any;
    if (typeof proto._canOpen === 'function') {
      proto._canOpen = function (this: any): boolean {
        return !this.panelOpen && !this.disabled && this.enabled !== false;
      };
    }

    const container = this.overlayContainer.getContainerElement();

    this.observer = new MutationObserver((mutations) => {
      const panelsToRefresh = new Set<HTMLElement>();
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const panel = node.matches('.mat-mdc-select-panel') ? node : (node.querySelector('.mat-mdc-select-panel') as HTMLElement | null);
          if (panel) {
            queueMicrotask(() => this.attachSearch(panel));
            return;
          }
          const host = node.closest('.mat-mdc-select-panel') as HTMLElement | null;
          if (host && host.classList.contains(MatSelectSearchService.MARK_CLASS) && (node.matches('mat-option, .mat-mdc-option') || node.querySelector('mat-option, .mat-mdc-option'))) {
            panelsToRefresh.add(host);
          }
        });
      }
      panelsToRefresh.forEach((panel) => {
        queueMicrotask(() => {
          const filterFn = this.filterFns.get(panel);
          if (!filterFn) return;
          const input = panel.querySelector<HTMLInputElement>('.app-select-search input');
          filterFn((input?.value ?? '').trim().toLowerCase());
        });
      });
    });

    this.observer.observe(container, { childList: true, subtree: true });
  }

  /** Creates a row that looks like a disabled mat-option. */
  private makeStatusRow(text: string, cls: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = cls;
    el.setAttribute('aria-disabled', 'true');
    el.style.cssText = [
      'display:none',
      'align-items:center',
      'min-height:36px',
      'padding:0 12px',
      'font-size:14px',
      'color:rgba(0,0,0,0.38)',
      'pointer-events:none',
      'user-select:none',
      'box-sizing:border-box',
    ].join(';');
    el.textContent = text;
    return el;
  }

  private attachSearch(panel: HTMLElement): void {
    if (panel.classList.contains(MatSelectSearchService.OPT_OUT_CLASS)) return;

    // ── Panel reused on subsequent opens (CDK reattaches the same DOM) ──────
    if (panel.classList.contains(MatSelectSearchService.MARK_CLASS)) {
      const filterFn = this.filterFns.get(panel);
      if (!filterFn) return;
      const input = panel.querySelector<HTMLInputElement>('.app-select-search input');
      if (input) input.value = '';
      // Re-evaluate live option count and set all visibility correctly
      filterFn('');
      setTimeout(() => {
        if (panel.querySelector<HTMLElement>('.app-select-search')?.style.display !== 'none') {
          input?.focus();
        }
      }, 0);
      return;
    }

    // ── First time this panel is decorated ───────────────────────────────────
    panel.classList.add(MatSelectSearchService.MARK_CLASS);

    // Reset any leftover display overrides from CDK portal reuse
    panel.querySelectorAll<HTMLElement>('mat-option, .mat-mdc-option').forEach((opt) => opt.style.removeProperty('display'));

    // ── Search bar ────────────────────────────────────────────────────────────
    const wrapper = document.createElement('div');
    wrapper.className = 'app-select-search';
    wrapper.style.cssText = ['position:sticky', 'top:0', 'z-index:10', 'background:#fff', 'padding:8px', 'border-bottom:1px solid #e2e8f0'].join(';');

    const fieldWrap = document.createElement('div');
    fieldWrap.style.cssText = ['position:relative', 'display:block', 'width:100%'].join(';');

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search';
    input.autocomplete = 'off';
    input.style.cssText = ['width:100%', 'padding:6px 28px 6px 10px', 'border:1px solid #e2e8f0', 'border-radius:6px', 'outline:none', 'font-size:14px', 'box-sizing:border-box'].join(';');

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.tabIndex = -1;
    clearBtn.setAttribute('aria-label', 'Clear search');
    clearBtn.innerHTML = '<span class="material-icons" style="font-size:16px;line-height:16px;">close</span>';
    clearBtn.style.cssText = [
      'position:absolute',
      'top:50%',
      'right:6px',
      'transform:translateY(-50%)',
      'background:transparent',
      'border:none',
      'cursor:pointer',
      'visibility:hidden',
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'width:20px',
      'height:20px',
      'padding:0',
      'margin:0',
      'color:#94a3b8',
    ].join(';');

    fieldWrap.appendChild(input);
    fieldWrap.appendChild(clearBtn);
    wrapper.appendChild(fieldWrap);
    panel.insertBefore(wrapper, panel.firstChild);

    // ── Status rows (styled as disabled mat-option rows) ──────────────────────
    const noDataRow = this.makeStatusRow('No data is available', 'app-select-no-data');
    panel.appendChild(noDataRow);

    const noResultsRow = this.makeStatusRow('No matching results', 'app-select-no-results');
    panel.appendChild(noResultsRow);

    // ── Filter — re-queries live options on every call ────────────────────────
    const applyFilter = (term: string): void => {
      const liveOptions = Array.from(panel.querySelectorAll<HTMLElement>('mat-option, .mat-mdc-option'));
      const liveGroups = Array.from(panel.querySelectorAll<HTMLElement>('mat-optgroup, .mat-mdc-optgroup'));
      const hasOptions = liveOptions.length > 0;
      let visibleCount = 0;

      liveOptions.forEach((opt) => {
        const show = !term || (opt.textContent ?? '').toLowerCase().includes(term);
        if (show) {
          opt.style.removeProperty('display');
          visibleCount++;
        } else {
          opt.style.setProperty('display', 'none');
        }
      });

      liveGroups.forEach((g) => {
        const anyVisible = Array.from(g.querySelectorAll<HTMLElement>('mat-option, .mat-mdc-option')).some((o) => o.style.display !== 'none');
        g.style.display = anyVisible ? '' : 'none';
      });

      // State 1 / 3: show search bar only when there are options
      wrapper.style.display = hasOptions ? '' : 'none';
      // State 3: no options at all
      noDataRow.style.display = hasOptions ? 'none' : 'flex';
      // State 2: filtering returned zero hits
      noResultsRow.style.display = hasOptions && visibleCount === 0 && term !== '' ? 'flex' : 'none';

      clearBtn.style.visibility = term ? 'visible' : 'hidden';
    };

    this.filterFns.set(panel, applyFilter);

    // ── Events ────────────────────────────────────────────────────────────────
    const allowBubble = new Set(['Escape', 'Tab']);

    const getVisibleOptions = (): HTMLElement[] => Array.from(panel.querySelectorAll<HTMLElement>('mat-option, .mat-mdc-option')).filter((opt) => opt.style.display !== 'none');

    const setActiveOption = (opts: HTMLElement[], idx: number): void => {
      panel.querySelectorAll<HTMLElement>('.mat-mdc-option-active').forEach((o) => o.classList.remove('mat-mdc-option-active'));
      if (idx >= 0 && idx < opts.length) {
        opts[idx].classList.add('mat-mdc-option-active');
        opts[idx].scrollIntoView({ block: 'nearest' });
      }
    };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.stopPropagation();
        e.preventDefault();
        const visible = getVisibleOptions();
        if (!visible.length) return;
        const current = panel.querySelector<HTMLElement>('.mat-mdc-option-active');
        let idx = current ? visible.indexOf(current) : -1;
        idx = e.key === 'ArrowDown' ? (idx < visible.length - 1 ? idx + 1 : 0) : idx > 0 ? idx - 1 : visible.length - 1;
        setActiveOption(visible, idx);
        return;
      }

      if (e.key === 'Enter') {
        const activeOption = panel.querySelector<HTMLElement>('.mat-mdc-option-active');
        const isActiveVisible = !!activeOption && activeOption.style.display !== 'none';
        e.stopPropagation();
        e.preventDefault();
        if (isActiveVisible) activeOption.click();
        return;
      }

      if (!allowBubble.has(e.key)) e.stopPropagation();
    });
    input.addEventListener('click', (e) => e.stopPropagation());
    input.addEventListener('input', () => {
      panel.querySelectorAll<HTMLElement>('.mat-mdc-option-active').forEach((o) => o.classList.remove('mat-mdc-option-active'));
      applyFilter(input.value.trim().toLowerCase());
    });

    clearBtn.addEventListener('mousedown', (e) => e.preventDefault());
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      input.value = '';
      applyFilter('');
      input.focus();
    });

    // Establish initial visibility, then focus the input if search bar is shown
    applyFilter('');
    setTimeout(() => {
      if (wrapper.style.display !== 'none') input.focus();
    }, 0);
  }
}
